import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    Upload,
    Image
} from 'antd';

import { PlusOutlined } from '@ant-design/icons';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';
import orgCategoryData from '../../../data/orgCategoryData';
import type { GetProp, UploadFile, UploadProps } from 'antd';
type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];


interface OrganizationModelProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        organization_id?: string;
        organization_name?: string;
        organization_category?: string;
        organization_class_id?: number;
        organization_description: string;
        organization_address: string;
        organization_mobile: number;
        organization_email: string;
        organization_website: string;
        organization_city: string;
        organization_state: string;
        organization_country: string;
        organization_logo?: string;
        organization_pincode: number;
        session_name: string;
        organization_active_session: string;
    } | null;
    isEditMode?: boolean;
}

const OrganizationModel: React.FC<OrganizationModelProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);
    const [fileList, setLogo] = useState<UploadFile[]>([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState<string>('');
    const [loading, setLoading] = useState(false);


    const [sessionOptions, setSessionOptions] = useState<any[]>([]);

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };

    // Handle form submit
    const handleAddOrganization = async (values: any) => {
        try {
            const formData = new FormData();

            // Append existing fields
            const dataVal = {
                organization_name: values.organization_name,
                organization_category: values.organization_category,
                organization_description: values.organization_description,
                organization_address: values.organization_address,
                organization_mobile: values.organization_mobile,
                organization_email: values.organization_email,
                organization_website: values.organization_website,
                organization_city: values.organization_city,
                organization_state: values.organization_state,
                organization_country: values.organization_country,
                organization_pincode: values.organization_pincode,
                organization_active_session: values.organization_active_session,
                ...(initialValues?.organization_id && { organization_id: initialValues.organization_id }),
            };

            // Append each field to FormData
            Object.entries(dataVal).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    formData.append(key, value);
                }
            });

            // Append file if available
            if (fileList.length > 0 && fileList[0]?.originFileObj) {
                formData.append('attachments', fileList[0].originFileObj);
            }

            // Determine the request URL and method
            const url = isEditMode ? 'edit-organization' : 'add-organization';
            const method = isEditMode ? 'put' : 'post';

            // Send the request
            const data = await apiRequest({
                method,
                url,
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            // console.log(dataVal);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'Organization updated successfully!' : 'Organization created successfully!',
            );
            form.resetFields();

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} organization`);
        }
    };

    // Upload button for Upload component
    const uploadButton = (
        <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
        </div>
    );

    const getBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    // image preview
    const handlePreview = async (file: any) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                organization_name: initialValues.organization_name,
                organization_category: initialValues.organization_category || undefined,
                organization_logo: initialValues.organization_logo,
                organization_description: initialValues.organization_description,
                organization_address: initialValues.organization_address,
                organization_mobile: initialValues.organization_mobile,
                organization_email: initialValues.organization_email,
                organization_website: initialValues.organization_website,
                organization_city: initialValues.organization_city,
                organization_state: initialValues.organization_state,
                organization_country: initialValues.organization_country,
                organization_pincode: initialValues.organization_pincode,
                organization_active_session: initialValues.organization_active_session,
            });

            // If organization_logo exists, show in fileList for preview
            if (initialValues.organization_logo) {
                setLogo([
                    {
                        uid: '-1',
                        name: 'image.png',
                        status: 'done',
                        url: `${process.env.REACT_APP_FILE_URL}uploads/organizations/logo/${initialValues.organization_logo}`,
                    },
                ]);
            } else {
                setLogo([]);
            }
        } else if (visible) {
            form.resetFields();
            setLogo([]);
        }
    }, [visible, isEditMode, initialValues, form]);

    useEffect(() => {
        if (visible) {
          const fetchData = async () => {
            setLoading(true);
            try {
              const [sessionRes] = await Promise.all([
                apiRequest({ method: 'get', url: '/sessions' }),
              ]);
              setSessionOptions(sessionRes);
            } catch {
              showError('Failed to load session');
            } finally {
              setLoading(false);
            }
          };
          fetchData();
        }
      }, [visible]);

    return (
        <Modal
            title={isEditMode ? 'Edit Organization' : 'Add New Organization'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddOrganization}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="organization_name"
                            label={
                                <span>
                                    Organization Name&nbsp;
                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                        (Max 255 characters)
                                    </span>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please enter the Organization Name' },
                                { max: 255, message: 'Maximum 255 characters allowed' },
                            ]}
                        >
                            <Input
                                showCount
                                maxLength={255}
                                placeholder="e.g., Organization "

                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="organization_category"
                            label="Organization category"
                            rules={[]}
                        >
                            <Select placeholder="Select Class" loading={loading} allowClear>
                                {orgCategoryData.map((cat) => (
                                    <Select.Option key={cat.category_value} value={cat.category_value}>
                                        {cat.category_name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    {/* Right column - Upload Image Centered Vertically */}
                    <Col
                        span={12}
                    >
                        <Form.Item
                            // name="organization_logo"
                            label="Logo"
                            rules={[
                                { required: !isEditMode, message: 'Please upload a logo!' },
                            ]}
                        >
                            <Upload
                                listType="picture-card"
                                fileList={fileList}
                                onPreview={handlePreview}
                                onChange={({ fileList }) => setLogo(fileList)}
                                beforeUpload={() => false} // prevent automatic upload
                                maxCount={1} // only one image
                                showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                                accept="image/jpeg,image/png,image/webp"
                            >
                                {fileList.length >= 1 ? null : uploadButton}
                            </Upload>

                            {previewImage && (
                                <Image
                                    wrapperStyle={{ display: 'none' }}
                                    preview={{
                                        visible: previewOpen,
                                        onVisibleChange: (visible) => setPreviewOpen(visible),
                                        afterOpenChange: (visible) => !visible && setPreviewImage(''),
                                    }}
                                    src={previewImage}
                                />
                            )}

                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="organization_description"
                            label="Description"
                            rules={[{ required: true, message: 'Please enter a description' }]}
                        >
                            <Input.TextArea rows={3} placeholder="Enter description" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="organization_address"
                            label="Address"
                            rules={[{ required: true, message: 'Please enter address' }]}
                        >
                            <Input placeholder="Enter address" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="organization_mobile"
                            label="Mobile"
                            rules={[
                                { required: true, message: 'Please enter mobile number' },
                                { pattern: /^\d{10}$/, message: 'Mobile must be 10 digits' }
                            ]}
                        >
                            <Input placeholder="Enter mobile number" type="number" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="organization_email"
                            label="Email"
                            rules={[
                                { required: true, message: 'Please enter email' },
                                { type: 'email', message: 'Invalid email format' }
                            ]}
                        >
                            <Input placeholder="Enter email" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="organization_website"
                            label="Website"
                            rules={[{ type: 'url', message: 'Enter a valid URL' }]}
                        >
                            <Input placeholder="e.g., https://example.org" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="organization_city"
                            label="City"
                            rules={[{ required: true, message: 'Please enter city' }]}
                        >
                            <Input placeholder="Enter city" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="organization_state"
                            label="State"
                            rules={[{ required: true, message: 'Please enter State' }]}
                        >
                            <Input placeholder="Enter State" />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item
                            name="organization_country"
                            label="Country"
                            rules={[{ required: true, message: 'Please enter country' }]}
                        >
                            <Input placeholder="Enter country" />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item
                            name="organization_pincode"
                            label="Pincode"
                            rules={[
                                { required: true, message: 'Please enter pincode' },
                                { pattern: /^\d{5,6}$/, message: 'Enter a valid pincode' }
                            ]}
                        >
                            <Input placeholder="Enter pincode" type="number" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="organization_active_session"
                            label="Session"
                        >
                            <Select placeholder="Select Class" loading={loading} allowClear>
                                {sessionOptions.map((s) => (
                                    <Select.Option key={s.session_id} value={s.session_id}>
                                        {s.session_name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Organization
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default OrganizationModel;
